import {
  LOCAL_API_CONNECT_BUDGET_MS,
  LOCAL_API_VERSION,
  LocalServerFrame,
  RivetsApiError,
  localApiBaseUrl,
  LOCAL_SESSION_TTL_MS,
  type LocalClientFrame,
  type LocalGrantSummary,
  type LoopbackTransportDescriptor,
  type ProvisionalChatEvent,
  type RivetsClient,
  type SnapshotSummary,
} from "@rivets/core";

/**
 * The browser's side of LocalApi (K4): a session with the worker's daemon on
 * this machine, over its loopback listener.
 *
 * It is an optimisation with no authority of its own. The relay mints a
 * one-use ticket, the daemon redeems it through the API for a grant, and
 * everything read here is either the worker's disk or provisional until the
 * relay confirms it. Any failure — the socket not opening inside
 * `LOCAL_API_CONNECT_BUDGET_MS`, a refused hello, a dropped socket, a request
 * past its deadline — sends every read back to the relay. A refusal is
 * remembered for a while (a minute for an unreachable listener, ten for a
 * refused hello), never for the rest of the page (CONTRACT C5): anything
 * else is re-probed with backoff, and a worker coming back online is tried
 * again at once.
 *
 * Socket first, ticket second, as the direct terminal does: a browser on
 * another machine gets an instant refusal from `127.0.0.1` and never spends a
 * ticket (or an API round trip) finding that out.
 */

type SocketState = 0 | 1 | 2 | 3;

export interface LocalWebSocket {
  readonly readyState: SocketState;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  addEventListener(type: "open" | "close" | "error" | "message", listener: (event: Event | MessageEvent) => void): void;
}

export interface LocalApiHandlers {
  onWorktreeChanged?(worktreeId: string, summary: SnapshotSummary): void;
  onProvisional?(chatId: string, events: ProvisionalChatEvent[]): void;
  /** The session is gone: provisionals drop and reads return to the relay.
   * `negative` means the endpoint refused or did not answer and is left alone
   * for a while (`isLocalApiUnavailable`); otherwise (a closed socket after
   * sleep, a daemon restart, a timed-out handshake or read, an expired token)
   * a new session is tried after a backoff. */
  onClosed?(workerId: string, detail: { negative: boolean; wasReady: boolean }): void;
  /** The handshake finished; watched worktrees can switch to local reads. */
  onReady?(workerId: string): void;
}

export interface LocalApiDeps {
  api: Pick<RivetsClient, "createLocalSession">;
  webSocket?: (url: string) => LocalWebSocket;
  fetch?: typeof fetch;
  now?: () => number;
  connectBudgetMs?: number;
  handshakeTimeoutMs?: number;
  requestTimeoutMs?: number;
}

/** Thrown when LocalApi cannot answer; the caller reads through the relay. */
export class LocalUnavailableError extends Error {
  constructor(readonly reason: string) {
    super(`LocalApi unavailable: ${reason}`);
    this.name = "LocalUnavailableError";
  }
}

const HANDSHAKE_TIMEOUT_MS = 5_000;
/** A local read slower than this goes to the relay instead (CONTRACT C5): a
 * daemon that accepted the connection but stalls must not hold a pane on a
 * skeleton. */
export const LOCAL_REQUEST_TIMEOUT_MS = 5_000;
/** One slow read (a big diff on a big repo) is about that read: only this
 * many in a row, with nothing answered between them, ends the session
 * (review #11). */
export const LOCAL_TIMEOUTS_BEFORE_CLOSE = 3;
const RENEW_FRACTIONS = [0.8, 0.9, 0.95] as const;
/** How long a listener that did not answer at all is left alone. */
export const UNREACHABLE_RETRY_MS = 60_000;
/** How long a refused hello (a ticket for another worker, a bad endpoint) or
 * a revocation is remembered. */
export const REFUSAL_MEMORY_MS = 10 * 60_000;

/** Workers whose LocalApi refused this page, and until when. */
const unavailable = new Map<string, number>();
const keyFor = (workerId: string, endpoint: string) => `${workerId}\u0000${endpoint}`;

export function isLocalApiUnavailable(workerId: string, endpoint: string, now = Date.now()): boolean {
  const until = unavailable.get(keyFor(workerId, endpoint));
  if (until === undefined) return false;
  if (now < until) return true;
  unavailable.delete(keyFor(workerId, endpoint));
  return false;
}

/** The relay says the worker is online again (a restart, perhaps on a new
 * port): whatever was refused or unreachable for it is forgotten. */
export function forgetLocalApiRefusals(workerId: string): void {
  for (const key of unavailable.keys()) if (key.startsWith(`${workerId}\u0000`)) unavailable.delete(key);
}

/** Waking or coming back online: unreachable listeners are worth trying
 * again now; refused ones are not. */
export function forgetUnreachableLocalApis(now = Date.now()): void {
  for (const [key, until] of unavailable) if (until - now <= UNREACHABLE_RETRY_MS) unavailable.delete(key);
}

export type LocalSessionState = "connecting" | "ready" | "closed";

export class LocalApiSession {
  state: LocalSessionState = "connecting";
  private socket?: LocalWebSocket;
  private token?: string;
  private grant?: LocalGrantSummary;
  private renewTimer?: ReturnType<typeof setTimeout>;
  private handshakeTimer?: ReturnType<typeof setTimeout>;
  private readonly watched = new Set<string>();
  private readonly chats = new Set<string>();
  /** Worktrees the daemon said this grant does not cover even after a re-mint. */
  private readonly refused = new Set<string>();
  /** Worktrees a renewal was already asked to cover: each gets one. */
  private readonly grantRefreshTried = new Set<string>();
  /** Worktrees waiting on the renewal in flight to say whether they are covered. */
  private readonly grantChecks = new Set<string>();
  private renewInFlight = false;
  private expiresAt?: number;
  private renewAttempt = 0;
  private consecutiveTimeouts = 0;
  /** Why the session ended, for diagnostics. */
  closeReason?: string;
  private readyWaiters: Array<(ready: boolean) => void> = [];
  readonly baseUrl: string;
  readonly started: number;

  constructor(
    readonly workerId: string,
    readonly descriptor: Extract<LoopbackTransportDescriptor, { state: "running" }>,
    private readonly deps: LocalApiDeps,
    private readonly handlers: LocalApiHandlers = {},
  ) {
    this.baseUrl = localApiBaseUrl(descriptor) ?? "";
    this.started = this.now();
  }

  private now(): number { return this.deps.now?.() ?? Date.now(); }

  /** Read through a getter so a state change across an `await` is seen. */
  private get closed(): boolean { return this.state === "closed"; }

  /** Resolves true once the session is ready, false if it failed. */
  whenReady(): Promise<boolean> {
    if (this.state === "ready") return Promise.resolve(true);
    if (this.state === "closed") return Promise.resolve(false);
    return new Promise((resolve) => this.readyWaiters.push(resolve));
  }

  covers(worktreeId: string): boolean {
    return this.state === "ready" && !this.refused.has(worktreeId) && (this.grant?.worktreeIds.includes(worktreeId) ?? false);
  }

  get canWrite(): boolean { return Boolean(this.grant?.canWrite && this.grant.orgActive); }

  async start(): Promise<boolean> {
    if (!this.baseUrl) return this.fail("not_advertised", "refused");
    let socket: LocalWebSocket;
    try {
      socket = this.makeSocket(`${this.baseUrl}/ws`);
    } catch {
      return this.fail("unreachable", "unreachable");
    }
    this.socket = socket;
    if (!await this.waitForOpen(socket)) {
      socket.close(1000, "unreachable");
      return this.fail("unreachable", "unreachable");
    }
    if (this.closed) return false;
    socket.addEventListener("message", (event) => this.receive((event as MessageEvent).data));
    // The socket opened, so this machine runs the listener: a drop now — a
    // daemon restarting mid-handshake, sleep, token expiry — is a reason to
    // try again after a backoff, never to give up on it (web #15).
    socket.addEventListener("close", () => this.fail("closed"));
    socket.addEventListener("error", () => this.fail("socket_error"));
    const ticket = await this.mintTicket();
    // A relay hiccup minting the ticket says nothing about this machine.
    if (!ticket || this.closed) return this.fail("ticket_failed");
    // The handshake includes the daemon redeeming the ticket through the API,
    // so a slow API moment can time it out: retried, not remembered.
    this.handshakeTimer = setTimeout(() => this.fail("handshake_timeout"), this.deps.handshakeTimeoutMs ?? HANDSHAKE_TIMEOUT_MS);
    if (!this.sendFrame({ type: "local.hello", ticket, version: LOCAL_API_VERSION })) return this.fail("hello_send_failed");
    return this.whenReady();
  }

  close(reason = "client_closed"): void {
    if (this.state === "closed") return;
    this.state = "closed";
    this.clearTimers();
    const socket = this.socket;
    this.socket = undefined;
    socket?.close(1000, reason);
    this.settleWaiters(false);
  }

  watchWorktree(worktreeId: string): void {
    if (this.watched.has(worktreeId)) return;
    this.watched.add(worktreeId);
    if (this.covers(worktreeId)) this.sendFrame({ type: "watch.worktree", worktreeId });
  }

  unwatchWorktree(worktreeId: string): void {
    if (!this.watched.delete(worktreeId)) return;
    if (this.covers(worktreeId)) this.sendFrame({ type: "unwatch.worktree", worktreeId });
  }

  isWatching(worktreeId: string): boolean {
    return this.state === "ready" && this.watched.has(worktreeId) && this.covers(worktreeId);
  }

  subscribeChat(chatId: string): void {
    if (this.chats.has(chatId)) return;
    this.chats.add(chatId);
    if (this.state === "ready") this.sendFrame({ type: "subscribe.chat", chatId });
  }

  unsubscribeChat(chatId: string): void {
    if (!this.chats.delete(chatId)) return;
    if (this.state === "ready") this.sendFrame({ type: "unsubscribe.chat", chatId });
  }

  /**
   * One authenticated request. An HTTP error answer (a rejected path, a
   * missing file, a vanished `lsnap_`, a busy daemon) is about this request
   * only: it comes back as `RivetsApiError` and the session stays up; the
   * caller decides (410 reloads the summary, anything else reads the relay).
   * Only a transport failure or a token the daemon no longer knows ends the
   * session — and even then it is retried after a backoff, not given up on.
   */
  async request<T>(worktreeId: string | null, path: string, init: { method?: "GET" | "POST"; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
    if (this.state !== "ready" || !this.token) throw new LocalUnavailableError("not_ready");
    const f = this.deps.fetch ?? fetch;
    const deadline = AbortSignal.timeout(this.deps.requestTimeoutMs ?? LOCAL_REQUEST_TIMEOUT_MS);
    const signal = init.signal ? AbortSignal.any([init.signal, deadline]) : deadline;
    let response: Response;
    let parsed: unknown;
    try {
      response = await f(`${this.baseUrl}${path}`, {
        method: init.method ?? "GET",
        headers: {
          authorization: `Bearer ${this.token}`,
          ...(init.body !== undefined ? { "content-type": "application/json" } : {}),
        },
        body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
        signal,
        // Never send cookies to the loopback listener.
        credentials: "omit",
      });
      parsed = response.ok ? await response.json() : await response.json().catch(() => null);
    } catch (error) {
      if (init.signal?.aborted) throw error;
      if (deadline.aborted) {
        // This read goes to the relay. The session stays up for the next one
        // — its control socket is what says whether the daemon is gone — unless
        // the daemon has stopped answering reads at all.
        this.consecutiveTimeouts += 1;
        if (this.consecutiveTimeouts >= LOCAL_TIMEOUTS_BEFORE_CLOSE) this.fail("request_timeout");
        throw new LocalUnavailableError("request_timeout");
      }
      // The listener refused or dropped the connection: the session closes
      // (and is re-probed) and this read goes to the relay.
      this.fail("request_failed");
      throw new LocalUnavailableError("request_failed");
    }
    this.consecutiveTimeouts = 0;
    if (response.ok) return parsed as T;
    const body = parsed as { code?: string; message?: string } | null;
    if (response.status === 401) {
      this.fail("unauthorized");
      throw new LocalUnavailableError("unauthorized");
    }
    if (response.status === 403 && body?.code === "worktree_not_granted" && worktreeId) {
      // The daemon disagrees with the grant we hold: one renewal, then relay.
      if (this.grantRefreshTried.has(worktreeId)) this.refused.add(worktreeId);
      else this.askGrantFor(worktreeId);
      throw new LocalUnavailableError("worktree_not_granted");
    }
    throw new RivetsApiError(response.status, body?.message ?? response.statusText, body?.code);
  }

  private async mintTicket(): Promise<string | undefined> {
    try {
      const ticket = await this.deps.api.createLocalSession({ workerId: this.workerId, audience: this.descriptor.audience });
      // The loopback peer is whatever process won this port. Never hand it a
      // ticket minted for anything but this exact worker and audience.
      if (ticket.audience !== this.descriptor.audience
        || ticket.workerThumbprint !== this.descriptor.workerThumbprint
        || ticket.expiresAt <= this.now()) return undefined;
      return ticket.ticket;
    } catch {
      return undefined;
    }
  }

  /**
   * A worktree created after this session's grant was minted is not in it,
   * and the daemon never gets a request for it to refuse. Asking for it
   * triggers one renewal (deduplicated with any renewal already in flight);
   * if the renewed grant still does not cover it, it stays on the relay.
   */
  refreshGrantFor(worktreeId: string): void {
    if (this.state !== "ready" || this.covers(worktreeId) || this.refused.has(worktreeId)) return;
    if (this.grantRefreshTried.has(worktreeId)) return;
    this.askGrantFor(worktreeId);
  }

  private askGrantFor(worktreeId: string): void {
    this.grantRefreshTried.add(worktreeId);
    this.grantChecks.add(worktreeId);
    if (!this.renewInFlight) void this.renew();
  }

  /** The renewal in flight has answered (or failed): every worktree that was
   * waiting on it is now either covered or given to the relay. */
  private settleGrantChecks(): void {
    for (const worktreeId of this.grantChecks) {
      if (!this.grant?.worktreeIds.includes(worktreeId)) this.refused.add(worktreeId);
    }
    this.grantChecks.clear();
  }

  private async renew(): Promise<void> {
    if (this.state !== "ready" || this.renewInFlight) return;
    this.renewInFlight = true;
    const ticket = await this.mintTicket();
    if (this.state !== "ready") return;
    if (!ticket || !this.sendFrame({ type: "local.renew", ticket })) {
      this.renewInFlight = false;
      this.settleGrantChecks();
      this.retryRenew();
    }
  }

  /** Renewal is tried at 80%, then 90% and 95% of the token's life. Past the
   * last try the token expires, the daemon closes the socket, and the session
   * reconnects with a fresh ticket after a backoff. */
  private scheduleRenew(): void {
    if (this.renewTimer) clearTimeout(this.renewTimer);
    this.renewTimer = undefined;
    const fraction = RENEW_FRACTIONS[this.renewAttempt];
    if (fraction === undefined || this.expiresAt === undefined) return;
    const renewAt = this.expiresAt - Math.round(LOCAL_SESSION_TTL_MS * (1 - fraction));
    this.renewTimer = setTimeout(() => void this.renew(), Math.max(0, renewAt - this.now()));
  }

  private retryRenew(): void {
    this.renewAttempt += 1;
    this.scheduleRenew();
  }

  private receive(raw: unknown): void {
    if (typeof raw !== "string" || this.state === "closed") return;
    let value: unknown;
    try { value = JSON.parse(raw); } catch { return; }
    const parsed = LocalServerFrame.safeParse(value);
    if (!parsed.success) return;
    const frame = parsed.data;
    switch (frame.type) {
      case "local.hello.result":
        if (!frame.accepted) {
          // A busy or rate-limited daemon, or a ticket that aged in transit,
          // is worth another try after the backoff; anything else is a
          // refusal of this page.
          const transient = frame.code === "capacity" || frame.code === "rate_limited" || frame.code === "expired";
          this.fail(`rejected_${frame.code}`, transient ? undefined : "refused");
          return;
        }
        this.adopt(frame.token, frame.expiresAt, frame.grant);
        if (this.state === "connecting") {
          if (this.handshakeTimer) clearTimeout(this.handshakeTimer);
          this.handshakeTimer = undefined;
          this.state = "ready";
          // Only granted worktrees: the daemon closes the socket on a watch
          // outside the grant, which would take every other worktree with it.
          for (const worktreeId of this.watched) if (this.covers(worktreeId)) this.sendFrame({ type: "watch.worktree", worktreeId });
          for (const chatId of this.chats) this.sendFrame({ type: "subscribe.chat", chatId });
          this.settleWaiters(true);
          this.handlers.onReady?.(this.workerId);
        }
        return;
      case "local.renew.result":
        // A refused renewal leaves the current token valid until it expires:
        // try again later in its life.
        this.renewInFlight = false;
        if (!frame.accepted) {
          this.settleGrantChecks();
          this.retryRenew();
          return;
        }
        {
          const before = new Set(this.grant?.worktreeIds ?? []);
          this.adopt(frame.token, frame.expiresAt, frame.grant);
          this.refused.clear();
          this.settleGrantChecks();
          // A renewed grant can cover worktrees opened since: watch them now.
          for (const worktreeId of this.watched) {
            if (!before.has(worktreeId) && this.covers(worktreeId)) this.sendFrame({ type: "watch.worktree", worktreeId });
          }
        }
        return;
      case "worktree.changed":
        if (this.watched.has(frame.worktreeId)) this.handlers.onWorktreeChanged?.(frame.worktreeId, frame.summary);
        return;
      case "chat.provisional":
        if (this.chats.has(frame.chatId)) this.handlers.onProvisional?.(frame.chatId, frame.events);
        return;
      case "local.revoked":
        this.fail("revoked", "refused");
        return;
      case "pong":
        return;
    }
  }

  private adopt(token: string, expiresAt: number, grant: LocalGrantSummary): void {
    this.token = token;
    this.grant = grant;
    this.expiresAt = expiresAt;
    this.renewAttempt = 0;
    this.scheduleRenew();
  }

  private sendFrame(frame: LocalClientFrame): boolean {
    if (!this.socket || this.socket.readyState !== 1) return false;
    try {
      this.socket.send(JSON.stringify(frame));
      return true;
    } catch {
      return false;
    }
  }

  /** Ends the session. `remember` keeps this endpoint off for a while: an
   * `unreachable` listener for a minute, a `refused` one for ten. */
  private fail(reason: string, remember?: "unreachable" | "refused"): false {
    if (this.state === "closed") return false;
    const negative = remember !== undefined;
    if (remember) unavailable.set(keyFor(this.workerId, this.descriptor.endpoint), this.now() + (remember === "unreachable" ? UNREACHABLE_RETRY_MS : REFUSAL_MEMORY_MS));
    const wasReady = this.state === "ready";
    this.closeReason = reason;
    this.close(reason);
    this.handlers.onClosed?.(this.workerId, { negative, wasReady });
    return false;
  }

  private settleWaiters(ready: boolean): void {
    const waiters = this.readyWaiters;
    this.readyWaiters = [];
    for (const resolve of waiters) resolve(ready);
  }

  private clearTimers(): void {
    if (this.renewTimer) clearTimeout(this.renewTimer);
    if (this.handshakeTimer) clearTimeout(this.handshakeTimer);
    this.renewTimer = this.handshakeTimer = undefined;
  }

  private makeSocket(url: string): LocalWebSocket {
    const factory = this.deps.webSocket ?? ((target: string) => new WebSocket(target) as unknown as LocalWebSocket);
    return factory(url.replace(/^http/, "ws"));
  }

  private waitForOpen(socket: LocalWebSocket): Promise<boolean> {
    if (socket.readyState === 1) return Promise.resolve(true);
    if (socket.readyState === 2 || socket.readyState === 3) return Promise.resolve(false);
    return new Promise((resolve) => {
      let settled = false;
      const finish = (value: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      };
      const timer = setTimeout(() => finish(false), this.deps.connectBudgetMs ?? LOCAL_API_CONNECT_BUDGET_MS);
      socket.addEventListener("open", () => finish(true));
      socket.addEventListener("error", () => finish(false));
      socket.addEventListener("close", () => finish(false));
    });
  }
}

/** Test seam: the negative cache is page-wide. */
export function resetLocalApiUnavailable(): void {
  unavailable.clear();
}
